import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Group, Paper, Text } from '@grovus/design-system'
import type { AccountId } from './data/account'
import { resolveClient, type Client } from './data/clients'
import { scopeKey, selectionKey } from './lib/counts'
import {
  DEFAULT_PERIOD, OLD_CONTRACT_PRESETS, PRESET_DAYS, periodKey, warmRangeFormatters, type Period,
} from './lib/period'
import type { Bounds } from './creatives/contract'
import { DICT, DICTIONARY_COPY, MEMBERS_COPY, REQUEST_COPY, SETTINGS_COPY, SHARE_COPY } from './i18n'
import type { ShellPreferences } from './lib/preferences'
import { AppShell, type AppShellNavItem } from './components/AppShell'
import { Header } from './components/Header'
import { SessionSettings } from './components/SessionSettings'
import { useNarrow } from './components/useNarrow'
import { useScopeCounts } from './components/useScopeCounts'
import type { ConfidenceThresholds } from './creatives/confidence'
import { createListReads } from './creatives/reads'
import { Sidebar, type Screen } from './components/Sidebar'
import type { Axis } from './hypotheses/pool'
import { HypothesesScreen } from './hypotheses/HypothesesScreen'
import { CreativesScreen, type CreativesScreenProps } from './creatives/CreativesScreen'
import {
  assignTags, decideSuggestion, mergeCreatives, setVersion as writeVersion, tagChange, type Decision, type DecisionOutcome,
  type MergeKeep, type TagChange,
} from './creatives/api'
import { DictionaryScreen } from './hypotheses/DictionaryScreen'
import { SettingsScreen } from './settings/SettingsScreen'
import { relabels, writeValue, type Axis as DictionaryAxis, type CreateOutcome, type DictionaryOp, type HypothesisValue, type WriteOutcome } from './hypotheses/dictionaryApi'
import { useDisclosurePanel } from './components/useDisclosurePanel'
import { useAccountConnections } from './components/useAccountConnections'
import { useCompanyCreation } from './components/useCompanyCreation'
import type { Intent, Notice } from './auth/entry'
import type { Reconciled } from './auth/reconcile'
import { TransitionNotice } from './members/TransitionNotice'
import { MembersPage, type AccessCheck } from './members/MembersPage'
import { ShareDialog } from './members/ShareDialog'
import { can } from './lib/roles'
import { useCompanyMembers } from './members/useCompanyMembers'
import { InvitationsPage } from './members/InvitationsPage'
import { PermissionScreen } from './requests/PermissionScreen'
import type { Invitations } from './members/useInvitations'
import type { GuestSession, SessionUser } from './auth/api'
import type { UserMenuAccount } from './components/UserMenu'
import { PeriodControl } from './components/PeriodControl'
import { GuestHeader } from './share/GuestHeader'

type SignOut = 'idle' | 'pending' | 'failed'

type Preset = NonNullable<CreativesScreenProps['preset']>

interface Props extends ShellPreferences {
  onSignOut: () => Promise<'ok' | 'error'>
  /** Every company this user works with, derived by the boundary. Never empty. */
  clients: Client[]
  activeId: string
  /** The signed-in user: the email for the user menu and the approval for "Add client". */
  user?: SessionUser
  onSelectClient?: (id: string) => void
  /**
   * Refetches every company and replaces `clients` with the answer, or resolves `null`
   * when it could not. The account panel calls it after every connect or removal attempt.
   */
  onRefreshClients?: () => Promise<Client[] | null>
  /** The signed-in user, whose table choices are remembered on this device. */
  userId?: string
  /**
   * The boundary's transition notice (GRO-106 plan, Task 6.4). It is a boundary prop, so
   * it survives App remounting through the transition that set it.
   */
  notice?: Notice | null
  onDismissNotice?: () => void
  /**
   * The boundary's `reconcile(intent?)` (GRO-106 design, "Reconciliation"): the Members
   * page re-checks lost access, refreshes the session after its own role changed, and
   * reports a leave through it.
   */
  onReconcile?: (intent?: Intent) => Promise<Reconciled>
  /** The session's received invitations, owned by the boundary. */
  invitations?: Invitations
  /** Set for a guest grant's read-only view (GRO-83); absent for a member. */
  guest?: GuestMode
}

/** What the boundary hands App for a guest view (GRO-83 design, "The guest view"). */
export interface GuestMode {
  session: GuestSession
  /** The last session re-read failed: Retry stands in for the reporting screens, which stay mounted and hidden. */
  unavailable: boolean
  /** Moves with every re-read that aborted the reporting requests. */
  reloadKey: number
  /** One session re-read; `abort: false` keeps the requests in flight. */
  onRecheck: (abort?: boolean) => Promise<void>
  /** Settles `false` when the server did not confirm the leave; the view stays. */
  onLeave: () => Promise<boolean>
}

const NO_REFRESH = async () => null
/** How long the account selection must hold still before the screens ask for it. */
const SELECTION_SETTLE_MS = 300
/** Scopes remembered with their selection, enough to resolve any report still in flight. */
const SCOPES_KEPT = 8

export default function App({
  onSignOut, lang, setLang, theme, toggleTheme, clients, activeId, user, onSelectClient,
  onRefreshClients = NO_REFRESH, userId, notice = null, onDismissNotice, onReconcile, invitations, guest,
}: Props) {
  const member = guest === undefined
  const [screen, setScreen] = useState<Screen>('creatives')
  // The comparison's axis, held here so the saved view "theme", the way back and a
  // re-drill land on the axis they left.
  const [axis, setAxis] = useState<Axis>('hypothesis')
  // The bucket a comparison row drilled into, cleared on the way out so the next drill
  // applies from a clean key however App mounts the two screens.
  const [bucket, setBucket] = useState<CreativesScreenProps['bucket']>(null)
  // Global and keyed by AccountId, seeded from every client's accounts rather than only
  // the active one's: the client narrows this map, it does not own it, and switching to a
  // company seeded after mount must not find every one of its accounts unchecked.
  const [accs, setAccs] = useState<Record<AccountId, boolean>>(
    () => Object.fromEntries(clients.flatMap((c) => c.accounts).map((a) => [a.id, true])),
  )
  const [signingOut, setSigningOut] = useState<SignOut>('idle')
  const [period, setPeriod] = useState<Period>(DEFAULT_PERIOD)
  // The shell falls back to the pre-GRO-65 contract when a screen reports a body from an
  // old server: an independent backend rollback, or a frontend that deployed first. It
  // offers 14/30/90 only and asks for no comparison until a response carries `bounds`.
  const [oldContract, setOldContract] = useState(false)
  // The range picker's limits, held under the selection they describe (company and
  // accounts, not the window), so a selection change drops them rather than letting one
  // company's history bound another's picker.
  const [bounds, setBounds] = useState<{ key: string; bounds: Bounds } | null>(null)
  // A range the new selection could not answer was replaced by the default preset. The
  // screens say so until the reader picks a period again.
  const [rangeRefused, setRangeRefused] = useState(false)
  // A saved view hands the screen a sort and a layout. Held in state, because the screen
  // applies it when the object identity changes: a literal would re-apply on every render.
  const [preset, setPreset] = useState<Preset | null>(null)
  // One account selection with two places to change it: the header's panel and the
  // creatives filter bar's toggles both write this map, never a copy of it (GRO-64, D5).
  const toggleAcc = useCallback((id: AccountId) => setAccs((prev) => ({ ...prev, [id]: prev[id] === false })), [])
  // The selection the screens ask for: `accs` after SELECTION_SETTLE_MS without a change,
  // so three toggles in a row send one request, for the last of them. The header and the
  // toggles render `accs` itself; only the request waits.
  const [settledAccs, setSettledAccs] = useState(accs)
  useEffect(() => {
    if (settledAccs === accs) return
    const timer = setTimeout(() => setSettledAccs(accs), SELECTION_SETTLE_MS)
    return () => clearTimeout(timer)
  }, [accs, settledAccs])

  const t = DICT[lang]
  // The screens are children, so their effects have sent the reads before this builds the
  // range formatters (GRO-180).
  useEffect(() => warmRangeFormatters(lang), [lang])
  const panels = useDisclosurePanel()
  const narrow = useNarrow()

  const activeClient = resolveClient(clients, activeId)

  // A `404` from a company-scoped call re-checks the session with a `lost` intent: `kept`
  // means the caller still belongs, and anything else has already moved the boundary on.
  const checkAccess = useCallback(async (companyId: string): Promise<AccessCheck> => {
    if (onReconcile === undefined) return 'failed'
    const result = await onReconcile({ kind: 'lost', companyId, activeAtStart: activeClient.id })
    if (result.status === 'failed') return 'failed'
    return result.status === 'ok' && result.outcome === 'kept' ? 'kept' : 'gone'
  }, [onReconcile, activeClient.id])
  // A reconciliation can change the caller's role, so the member rows are read again.
  const reloadMembers = useRef<() => void>(() => {})
  const refreshSession = useCallback(async () => {
    if (onReconcile === undefined) {
      await onRefreshClients()
      return
    }
    const result = await onReconcile()
    if (result.status === 'ok') reloadMembers.current()
  }, [onReconcile, onRefreshClients])
  // `false` when the read that should move the switcher on failed: the page that asked
  // (a leave or a delete) says so and offers the read again.
  const leaveCompany = useCallback(async (companyId: string): Promise<boolean> => {
    if (onReconcile !== undefined) return (await onReconcile({ kind: 'left', companyId })).status !== 'failed'
    return (await onRefreshClients()) !== null
  }, [onReconcile, onRefreshClients])
  const companyDeleted = useCallback(async (companyId: string): Promise<boolean> => {
    if (onReconcile !== undefined) return (await onReconcile({ kind: 'deleted', companyId })).status !== 'failed'
    return (await onRefreshClients()) !== null
  }, [onReconcile, onRefreshClients])
  // The signals of the design's "Lost access" list that have no refusal of their own to
  // show report here; the re-check alone decides whether the company is gone.
  const reportLost = useCallback((companyId: string) => { void checkAccess(companyId) }, [checkAccess])
  // A guest's list `404` means the company is not in this browser's grant any more: one
  // session re-read, which keeps the list's own notice and Retry on the same company.
  const guestRecheck = guest?.onRecheck
  const onCompanyNotFound = useCallback((companyId: string) => {
    if (guestRecheck !== undefined) void guestRecheck(false)
    else reportLost(companyId)
  }, [guestRecheck, reportLost])
  const companyMembers = useCompanyMembers(activeClient.id, reportLost, member)
  reloadMembers.current = companyMembers.reload

  // Raised per company whenever a connect or a demo removal changed its set of accounts,
  // and carried into the shared scope key and the creatives screen's row scope, so the
  // reporting, both counts and an open detail panel all follow the change.
  const [revisions, setRevisions] = useState<Record<string, number>>({})
  const accountsRevision = revisions[activeClient.id] ?? 0
  const onAccountsChanged = useCallback((clientId: string) => {
    setRevisions((prev) => ({ ...prev, [clientId]: (prev[clientId] ?? 0) + 1 }))
  }, [])
  // Per company: the revision of what the reporting screens show, raised when an assignment
  // or a relabel commits, and the revision of the assign picker's options, raised by any
  // dictionary write. Both are raised here, by the shell that issued the write, so a write
  // that lands after the screen that started it unmounted still refreshes whichever screen
  // shows that company next, and a company switched away from is refreshed on return.
  const [reportingRevisions, setReportingRevisions] = useState<Record<string, number>>({})
  const [pickerRevisions, setPickerRevisions] = useState<Record<string, number>>({})
  // One assignment at a time per company, held by the shell rather than the screen: a
  // creatives screen remounted while a write is out reads it here, so leaving and coming
  // back cannot submit a second assignment over the first.
  const [pendingAssignments, setPendingAssignments] = useState<Record<string, { creativeIds: string[] }>>({})
  const assigning = useRef(new Set<string>())
  // Runs `write` as the company's one pending assignment, or answers `stale` when one is out.
  const withAssignmentLock = useCallback(async <T,>(
    companyId: string,
    pending: { creativeIds: string[] },
    write: () => Promise<T>,
    busy: T,
  ): Promise<T> => {
    if (assigning.current.has(companyId)) return busy
    assigning.current.add(companyId)
    setPendingAssignments((prev) => ({ ...prev, [companyId]: pending }))
    try {
      return await write()
    } finally {
      assigning.current.delete(companyId)
      setPendingAssignments((prev) => {
        const { [companyId]: _done, ...rest } = prev
        return rest
      })
    }
  }, [])
  const assignUnlocked = useCallback(async (
    companyId: string,
    creativeIds: string[],
    change: TagChange,
  ): Promise<WriteOutcome<{ updated: number }>> => {
    const outcome = await assignTags(companyId, creativeIds, change)
    // A lost response may hide an assignment that committed and cleared version letters,
    // so `unreachable` refreshes the reporting too.
    if (outcome.status === 'ok' || outcome.status === 'unreachable') {
      setReportingRevisions((prev) => ({ ...prev, [companyId]: (prev[companyId] ?? 0) + 1 }))
    }
    if (outcome.status === 'notFound' || outcome.status === 'forbidden') {
      reportLost(companyId)
    }
    return outcome
  }, [reportLost])
  const assign = useCallback((
    companyId: string,
    creativeIds: string[],
    change: TagChange,
  ): Promise<WriteOutcome<{ updated: number }>> => withAssignmentLock(
    companyId,
    { creativeIds },
    () => assignUnlocked(companyId, creativeIds, change),
    { status: 'stale' },
  ), [withAssignmentLock, assignUnlocked])
  // The version letter (GRO-159) shares the assignment's lock, so a hypothesis write and a
  // version write of one company never overlap. A lost response may hide a write that
  // committed, so `unreachable` refreshes the reporting.
  const setVersion = useCallback((
    companyId: string,
    creativeIds: string[],
    version: string | null,
  ): Promise<WriteOutcome<{ updated: number }>> => withAssignmentLock(
    companyId,
    { creativeIds },
    async () => {
      const outcome = await writeVersion(companyId, creativeIds, version)
      if (outcome.status === 'ok' || outcome.status === 'unreachable') {
        setReportingRevisions((prev) => ({ ...prev, [companyId]: (prev[companyId] ?? 0) + 1 }))
      }
      if (outcome.status === 'notFound' || outcome.status === 'forbidden') {
        reportLost(companyId)
      }
      return outcome
    },
    { status: 'stale' },
  ), [withAssignmentLock, reportLost])
  // A merge (GRO-161) shares the assignment's lock: it rewrites the assignments of every
  // creative it takes. A lost response may hide a merge that committed, so `unreachable`
  // refreshes the reporting.
  const merge = useCallback((
    companyId: string,
    creativeIds: string[],
    keep: MergeKeep | null,
  ): Promise<WriteOutcome<{ creativeId: string }>> => withAssignmentLock(
    companyId,
    { creativeIds },
    async () => {
      const outcome = await mergeCreatives(companyId, creativeIds, keep)
      if (outcome.status === 'ok' || outcome.status === 'unreachable') {
        setReportingRevisions((prev) => ({ ...prev, [companyId]: (prev[companyId] ?? 0) + 1 }))
      }
      if (outcome.status === 'notFound' || outcome.status === 'forbidden') {
        reportLost(companyId)
      }
      return outcome
    },
    { status: 'stale' },
  ), [withAssignmentLock, reportLost])
  const writeDictionary = useCallback(async (
    companyId: string,
    op: DictionaryOp,
  ): Promise<WriteOutcome<HypothesisValue>> => {
    const outcome = await writeValue(companyId, op)
    if (outcome.status === 'ok') {
      setPickerRevisions((prev) => ({ ...prev, [companyId]: (prev[companyId] ?? 0) + 1 }))
      // The reporting reads labels, not claims or the archive flag: only a relabel moves it.
      if (relabels(op)) setReportingRevisions((prev) => ({ ...prev, [companyId]: (prev[companyId] ?? 0) + 1 }))
    } else if (outcome.status === 'notFound' || outcome.status === 'forbidden') {
      reportLost(companyId)
    }
    return outcome
  }, [reportLost])
  // A decision on a suggestion (GRO-173) shares the assignment's lock: an accept assigns,
  // and may create the value it names. A lost response may hide a decision that committed,
  // so `unreachable` refreshes the reporting, and after an accept the picker's options too.
  // A vanished suggestion is not a lost company.
  const decide = useCallback((
    companyId: string,
    creativeId: string,
    suggestionId: string,
    decision: Decision,
  ): Promise<DecisionOutcome> => withAssignmentLock<DecisionOutcome>(
    companyId,
    { creativeIds: [creativeId] },
    async () => {
      const outcome = await decideSuggestion(companyId, suggestionId, decision)
      if (outcome.status === 'ok' || outcome.status === 'unreachable') {
        setReportingRevisions((prev) => ({ ...prev, [companyId]: (prev[companyId] ?? 0) + 1 }))
        if (decision === 'accept') setPickerRevisions((prev) => ({ ...prev, [companyId]: (prev[companyId] ?? 0) + 1 }))
      }
      if (outcome.status === 'notFound' || outcome.status === 'forbidden') {
        reportLost(companyId)
      }
      return outcome
    },
    { status: 'stale' },
  ), [withAssignmentLock, reportLost])
  // "Create" in a picker (GRO-158, GRO-170): the create on `axis` and the assignment that
  // follows it are one pending operation. A lost response may hide a write that committed,
  // so `unreachable` refreshes what that write would have changed.
  const createAndAssign = useCallback((
    companyId: string,
    creativeIds: string[],
    name: string,
    axis: DictionaryAxis,
  ): Promise<CreateOutcome> => withAssignmentLock<CreateOutcome>(
    companyId,
    { creativeIds },
    async () => {
      const created = await writeDictionary(companyId, { kind: 'add', axis, draft: { name, claim: null } })
      if (created.status !== 'ok') {
        if (created.status === 'unreachable') {
          setPickerRevisions((prev) => ({ ...prev, [companyId]: (prev[companyId] ?? 0) + 1 }))
        }
        return { stage: 'create', outcome: created }
      }
      const change = tagChange(axis, created.body.id)
      const outcome = await assignUnlocked(companyId, creativeIds, change)
      if (outcome.status === 'unreachable') {
        setReportingRevisions((prev) => ({ ...prev, [companyId]: (prev[companyId] ?? 0) + 1 }))
      }
      return { stage: 'assign', value: created.body, outcome }
    },
    { stage: 'create', outcome: { status: 'stale' } },
  ), [withAssignmentLock, writeDictionary, assignUnlocked])
  // "Create" in a field of the selection bar (GRO-172): the value only, which the field then
  // holds until «Применить». It takes the same lock, so no tags write overlaps it.
  const createValue = useCallback((
    companyId: string,
    name: string,
    axis: DictionaryAxis,
  ): Promise<WriteOutcome<HypothesisValue>> => withAssignmentLock<WriteOutcome<HypothesisValue>>(
    companyId,
    { creativeIds: [] },
    async () => {
      const created = await writeDictionary(companyId, { kind: 'add', axis, draft: { name, claim: null } })
      if (created.status === 'unreachable') {
        setPickerRevisions((prev) => ({ ...prev, [companyId]: (prev[companyId] ?? 0) + 1 }))
      }
      return created
    },
    { status: 'stale' },
  ), [withAssignmentLock, writeDictionary])

  const connections = useAccountConnections(
    activeClient,
    onRefreshClients,
    onAccountsChanged,
    panels.isOpen('accounts'),
    undefined,
    member,
    user,
  )

  // A company the add-client flow confirmed (GRO-82) is selected only if the switcher is
  // still the open panel when it lands, read through a ref rather than a value captured
  // when the request left: switching the reporting scope under a reader who moved on to
  // something else would change every figure on screen without their asking.
  const openPanelRef = useRef(panels.openPanel)
  openPanelRef.current = panels.openPanel
  const selectRef = useRef(onSelectClient)
  selectRef.current = onSelectClient
  const closePanels = panels.close
  const onCreated = useCallback((companyId: string) => {
    if (openPanelRef.current !== 'client') return
    selectRef.current?.(companyId)
    closePanels()
  }, [closePanels])
  const creation = useCompanyCreation({
    userId,
    refreshClients: onRefreshClients,
    switcherOpen: panels.isOpen('client') && signingOut === 'idle',
    onCreated,
    enabled: member,
  })

  // Selecting a company sets the active id, persists it through the boundary and closes
  // the panel. It also clears row scope - and there is none to clear: the wireframe's
  // CLEAR_ROW_SCOPE is `{ sel, preview, hover, qFocus }`, and nothing in this shell or in
  // CreativesScreen holds any of the four yet. This is the point where it is cleared once
  // GRO-17 rebuilds the detail panel and gives the screen a selected row; it is empty
  // today and the PR says so rather than claiming the evaluation passes.
  function handleSelectClient(id: string) {
    onSelectClient?.(id)
  }

  async function handleSignOut() {
    setSigningOut('pending')
    // Drop anything derived from the session now, not when the POST answers: a sign-out
    // that never confirms must still leave nothing of the session behind. The boundary
    // aborts the requests that would otherwise refill it. Closing rather than dismissing
    // returns focus to the trigger: sign-out is pressed inside the user menu, and the
    // button that held focus goes with the menu (GRO-66).
    panels.close()
    const result = await onSignOut()
    if (result === 'error') setSigningOut('failed')
    // On 'ok' the auth boundary unmounts this component; no local state left to settle.
  }

  // Every saved view lands through `selectScreen`, never a bare `setScreen`: the
  // comparison view especially, because leaving the creatives screen must clear the
  // bucket the same way the nav does - a drill followed by "Theme comparison" followed
  // by the nav's Creatives item must not resurrect a narrowed list.
  function onSavedView(name: 'weekly' | 'thin' | 'theme') {
    if (name === 'weekly') {
      selectScreen('creatives')
      setPreset({ sort: 'spend', dir: 'desc', view: 'table' })
    } else if (name === 'thin') {
      selectScreen('creatives')
      setPreset({ sort: 'roas', dir: 'desc', view: 'grid' })
    } else {
      selectScreen('rollups')
      setAxis('hypothesis')
    }
  }

  // The checked map is global and the client narrows it, rather than the map being
  // re-seeded per client: that is what the wireframe does, and it removes the question of
  // what a re-seed owes an account the user deliberately unchecked. An account the map has
  // never heard of counts as checked: the map is seeded once, at mount, so a company
  // created afterwards has no entries, and only an explicit unchecking removes one.
  //
  // What the screens ask for. A company with no accounts, and one with every account
  // checked, ask for the server's default scope: sending every id verbatim would have a
  // first load refused in a company holding two currencies or two provenances, and the
  // default scope is the server's answer to exactly that. None checked is an explicit
  // empty selection and makes no request; a strict subset names its ids.
  const idsOf = (checked: Record<AccountId, boolean>): string[] | null => {
    const checkedIds = activeClient.accounts.filter((a) => checked[a.id] !== false).map((a) => a.id)
    return checkedIds.length === activeClient.accounts.length ? null : checkedIds
  }
  const accountIds = idsOf(settledAccs)

  // The shared reporting scope: what the shell owns and invalidates on. Each screen
  // extends it with its own view state, which only that screen can describe. It follows
  // the settled selection; `liveSelection` is the one on screen, and `settling` says the
  // two differ, so nothing read off a response is offered while the request waits.
  const liveSelection = selectionKey({ clientId: activeClient.id, accountIds: idsOf(accs), accountsRevision })
  const settledSelection = selectionKey({ clientId: activeClient.id, accountIds, accountsRevision })
  const settling = liveSelection !== settledSelection
  const scope = scopeKey({ clientId: activeClient.id, accountIds, period: periodKey(period), accountsRevision })
  const scopeRef = useRef(scope)
  scopeRef.current = scope
  const settlingRef = useRef(settling)
  settlingRef.current = settling
  // Every scope handed to the screens, with the selection it was computed from, so a
  // report resolves to the selection it answers however late it lands.
  const selectionOf = useRef(new Map<string, string>())
  if (!selectionOf.current.has(scope)) {
    selectionOf.current.set(scope, settledSelection)
    if (selectionOf.current.size > SCOPES_KEPT) {
      selectionOf.current.delete(selectionOf.current.keys().next().value!)
    }
  }
  const periodRef = useRef(period)
  periodRef.current = period
  // The bounds are offered only for the selection on screen: a toggle takes them away in
  // the render that makes it, and a return to the selection they describe gives them back
  // without a request.
  const pickerBounds = bounds !== null && bounds.key === liveSelection ? bounds.bounds : null
  // The empty state's way to the calendar exists only while the calendar can open; the
  // callback reads the same through a ref, so a stale closure cannot open an empty slot.
  const periodOpenable = !oldContract && pickerBounds !== null
  const periodOpenableRef = useRef(periodOpenable)
  periodOpenableRef.current = periodOpenable
  const openPanel = panels.open
  const onOpenPeriod = useCallback(() => {
    if (periodOpenableRef.current) openPanel('period')
  }, [openPanel])

  // A refusal waiting for its scope to be the one on screen again; see `onPeriodRefused`.
  const [heldRefusal, setHeldRefusal] = useState<{ scope: string } | null>(null)
  // The creatives screen's own prefetch, which it fills while it is mounted (GRO-180).
  const screenPrefetch = useRef<((next: Period, scope: string) => void) | null>(null)
  // The reader's own choice clears the notice a refused range left behind.
  //
  // A new period also starts its reads here, before the render that commits it: the
  // screens ask for the window only in an effect after that render, which is the whole
  // screen's work. The screen prefetches its own key and the shell the badge's, which is
  // also the Ideas base read; each read after the render joins the request it finds.
  const changePeriod = useCallback((next: Period) => {
    setRangeRefused(false)
    setHeldRefusal(null)
    if (periodKey(next) !== periodKey(periodRef.current)) {
      const shell = prefetchShell.current
      const nextScope = scopeKey({
        clientId: shell.clientId, accountIds: shell.accountIds, period: periodKey(next), accountsRevision: shell.accountsRevision,
      })
      screenPrefetch.current?.(next, nextScope)
      if (shell.reporting && !shell.noSelection) {
        shell.reads.prefetch(
          { companyId: shell.clientId, period: next, accountIds: shell.accountIds, audienceId: null, compare: false },
          { scope: nextScope, reloadKey: shell.reloadKey },
          fetch,
        )
      }
    }
    setPeriod(next)
  }, [])
  // A screen's response carries the bounds for the scope it was asked under, and they are
  // kept under that scope's selection, late or not: `pickerBounds` decides whether they
  // describe the selection on screen.
  const onBounds = useCallback((report: { scope: string; bounds: Bounds | null }) => {
    const key = selectionOf.current.get(report.scope)
    if (key === undefined || report.bounds === null) return
    setOldContract(false)
    setBounds({ key, bounds: report.bounds })
  }, [])
  const onOldContract = useCallback(() => {
    setOldContract(true)
    const current = periodRef.current
    if (current.kind === 'range' || !OLD_CONTRACT_PRESETS.includes(current.days)) setPeriod(DEFAULT_PERIOD)
  }, [])
  // A refusal resets the period, an action rather than a value, so it is taken only for
  // the scope on screen with the selection settled. Otherwise it waits, and is taken if
  // the reader comes back to that scope; one for a scope left for good never is.
  const applyRefusal = useCallback(() => {
    if (periodRef.current.kind !== 'range') return
    setRangeRefused(true)
    setPeriod(DEFAULT_PERIOD)
  }, [])
  const onPeriodRefused = useCallback((report: { scope: string }) => {
    if (report.scope === scopeRef.current && !settlingRef.current) applyRefusal()
    else setHeldRefusal({ scope: report.scope })
  }, [applyRefusal])
  useEffect(() => {
    if (heldRefusal === null || heldRefusal.scope !== scope || settling) return
    setHeldRefusal(null)
    applyRefusal()
  }, [heldRefusal, scope, settling, applyRefusal])
  // Leaving the creatives screen clears the bucket: through the bucket note's way back
  // and through the nav alike, so a re-drill re-applies from a cleared key even if a
  // later shell keeps both screens mounted.
  const selectScreen = useCallback((value: string) => {
    if (value !== 'creatives') setBucket(null)
    setScreen(value as Screen)
  }, [])
  // An approval the focus recheck reconciled ends the request: "Add client" is enabled again.
  const approved = user?.canCreateCompanies ?? true
  useEffect(() => {
    if (screen === 'permission' && approved) selectScreen('creatives')
  }, [screen, approved, selectScreen])

  // A guest's screens refetch after each re-read (GRO-83); a member's after each committed
  // assignment or relabel in the active company. Both only ever rise, so their sum moves
  // whenever either does.
  const reloadKey = (guest?.reloadKey ?? 0) + (reportingRevisions[activeClient.id] ?? 0)
  const canEditDictionary = member && can(activeClient.role, 'editDictionary')
  const canEditSettings = member && can(activeClient.role, 'editSettings')
  // A settings save bumps no data revision: the verdicts read the settings per request, so
  // the shell moves `reloadKey` itself (GRO-185).
  // The legend keeps the company's last read tiers while a new scope's read runs, so it
  // does not empty on every scope change; another company starts from no rows (GRO-186).
  const [legend, setLegend] = useState<{ companyId: string; thresholds: ConfidenceThresholds } | null>(null)
  const onSettingsSaved = useCallback((companyId: string) => {
    setReportingRevisions((prev) => ({ ...prev, [companyId]: (prev[companyId] ?? 0) + 1 }))
    // A save is the one event that makes the retained tiers wrong: the legend shows no rows
    // until a read returns the saved ones.
    setLegend((prev) => (prev !== null && prev.companyId === companyId ? null : prev))
  }, [])
  // A leave the server did not confirm still hides the figures: the reader asked to stop
  // seeing them, and the header offers the leave again.
  const [leaveUnconfirmed, setLeaveUnconfirmed] = useState(false)
  const reporting = guest === undefined || (!guest.unavailable && !leaveUnconfirmed)

  // Both badges are functions of the scope alone (GRO-177): the shell reads the scope once
  // and derives both numbers from that response, so neither the open screen nor a filter
  // inside it can move them. `settling` is its own argument rather than folded into
  // `enabled`: a settle-window bounce hides the counts without asking for anything, while
  // hidden reporting asks for nothing and keeps what it holds.
  // One store for every list read the shell's screens and badge issue (GRO-179): equal
  // requests share one read, and a screen that mounts again paints its last body first.
  const [reads] = useState(() => createListReads())
  // What `changePeriod` builds the next scope and the badge's key from: it is a stable
  // callback, so it reads the current values here rather than closing over them.
  const prefetchShell = useRef({ clientId: activeClient.id, accountIds, accountsRevision, reporting, reloadKey, reads, noSelection: false })
  prefetchShell.current = {
    clientId: activeClient.id, accountIds, accountsRevision, reporting, reloadKey, reads,
    noSelection: accountIds !== null && accountIds.length === 0,
  }
  const counts = useScopeCounts({
    reads,
    scope,
    companyId: activeClient.id,
    period,
    accountIds,
    enabled: reporting,
    settling,
    noSelection: accountIds !== null && accountIds.length === 0,
    reloadKey,
  })
  useEffect(() => {
    if (counts.thresholds !== null) setLegend({ companyId: activeClient.id, thresholds: counts.thresholds })
  }, [counts.thresholds, activeClient.id])
  const legendThresholds = legend !== null && legend.companyId === activeClient.id ? legend.thresholds : null

  const navItems: AppShellNavItem[] = [
    // An unknown count renders as no badge, which `AppShellNavItem.count` already allows.
    { value: 'creatives', label: t.creatives, count: counts.creatives ?? undefined },
    { value: 'rollups', label: t.rollups, count: counts.rollups ?? undefined },
    ...(member ? [{
      value: 'members',
      label: MEMBERS_COPY[lang].members,
      count: companyMembers.state.phase === 'ready' ? companyMembers.state.members.length : undefined,
    }] : []),
    ...(canEditSettings ? [{ value: 'settings', label: SETTINGS_COPY[lang].nav }] : []),
  ]

  // The user menu's account section (GRO-106). Leave is locked only for an Owner whose
  // `ready` member list holds one Owner; while the list is unknown, `409 last_owner` decides.
  const owners = companyMembers.state.phase === 'ready'
    ? companyMembers.state.members.filter((m) => m.role === 'owner').length
    : null
  const account: UserMenuAccount | undefined = user === undefined ? undefined : {
    email: user.email,
    invitationCount: invitations?.state.phase === 'ready' ? invitations.state.items.length : 0,
    onOpenInvitations: () => {
      panels.close()
      selectScreen('invitations')
    },
    company: userId === undefined ? undefined : {
      id: activeClient.id,
      name: activeClient.name,
      userId,
      leaveLocked: activeClient.role === 'owner' && owners === 1,
      onOpenMembers: () => {
        panels.close()
        selectScreen('members')
      },
      canShare: can(activeClient.role, 'share'),
      onShare: () => {
        panels.close()
        selectScreen('share')
      },
      onLeft: async (companyId) => {
        panels.close()
        await leaveCompany(companyId)
      },
    },
  }

  // The session settings live in the user menu at every width (GRO-66). They no longer
  // move between the header and the Menu section, so nothing tracks where focus was.
  const settings = (
    <SessionSettings
      t={t}
      lang={lang}
      setLang={setLang}
      theme={theme}
      toggleTheme={toggleTheme}
      onSignOut={handleSignOut}
      signOutPending={signingOut === 'pending'}
    />
  )

  return (
    <AppShell
      narrow={narrow}
      header={guest !== undefined ? (
        <GuestHeader
          t={t}
          lang={lang}
          setLang={setLang}
          theme={theme}
          toggleTheme={toggleTheme}
          companyName={guest.session.companyName}
          expiresAt={guest.session.expiresAt}
          onLeave={async () => {
            const left = await guest.onLeave()
            if (!left) setLeaveUnconfirmed(true)
            return left
          }}
          period={(
            <PeriodControl
              t={t}
              lang={lang}
              period={period}
              onChange={changePeriod}
              presets={oldContract ? OLD_CONTRACT_PRESETS : PRESET_DAYS}
              bounds={oldContract ? null : pickerBounds}
              narrow={narrow}
              rangeOpen={panels.isOpen('period')}
              rangeTriggerProps={panels.triggerProps('period')}
              rangePanelProps={panels.panelProps('period')}
              closeRange={panels.close}
            />
          )}
        />
      ) : (
        <Header
          t={t}
          client={activeClient}
          clients={clients}
          account={account}
          canCreate={user?.canCreateCompanies ?? true}
          onRequestPermission={() => selectScreen('permission')}
          onSelectClient={handleSelectClient}
          clientPanel={panels.isOpen('client') && signingOut === 'idle'}
          clientTriggerProps={panels.triggerProps('client')}
          clientPanelProps={panels.panelProps('client')}
          closePanel={panels.close}
          creation={creation}
          accs={accs}
          toggleAcc={toggleAcc}
          accPanel={panels.isOpen('accounts') && signingOut === 'idle'}
          accountTriggerProps={panels.triggerProps('accounts')}
          accountPanelProps={panels.panelProps('accounts')}
          connections={connections}
          lang={lang}
          period={period}
          onChangePeriod={changePeriod}
          presets={oldContract ? OLD_CONTRACT_PRESETS : PRESET_DAYS}
          bounds={oldContract ? null : pickerBounds}
          narrow={narrow}
          periodPanel={panels.isOpen('period') && signingOut === 'idle'}
          periodTriggerProps={panels.triggerProps('period')}
          periodPanelProps={panels.panelProps('period')}
          helpPanel={panels.isOpen('help') && signingOut === 'idle'}
          helpTriggerProps={panels.triggerProps('help')}
          helpPanelProps={panels.panelProps('help')}
          userPanel={panels.isOpen('user') && signingOut === 'idle'}
          userTriggerProps={panels.triggerProps('user')}
          userPanelProps={panels.panelProps('user')}
          signOutPending={signingOut === 'pending'}
          settings={settings}
        />
      )}
      navItems={navItems}
      // The dictionary is reached from the comparison and keeps its nav item lit.
      active={screen === 'dictionary' ? 'rollups' : screen}
      onSelect={selectScreen}
      navLabel={t.navLabel}
      navToggleLabel={t.navToggle}
      sidebarFooter={<Sidebar t={t} thresholds={legendThresholds} onSavedView={onSavedView} />}
      headingText={screen === 'creatives' ? t.creatives : screen === 'rollups' ? t.rollups : screen === 'dictionary' ? DICTIONARY_COPY[lang].title : screen === 'members' ? MEMBERS_COPY[lang].members : screen === 'permission' ? REQUEST_COPY[lang].permTitle : screen === 'share' ? SHARE_COPY[lang].share : screen === 'settings' ? SETTINGS_COPY[lang].title : MEMBERS_COPY[lang].invitations}
      bodyHidden={signingOut !== 'idle'}
      banner={signingOut === 'failed' ? (
        <Paper
          role="alert"
          withBorder
          radius={0}
          p="md"
          style={{
            // Keep these palette pairs aligned with the shared AlertBanner.
            background: 'light-dark(var(--mantine-color-bad-0), var(--mantine-color-bad-9))',
            borderColor: 'light-dark(var(--mantine-color-bad-1), var(--mantine-color-bad-8))',
            borderLeft: 0,
            borderRight: 0,
          }}
        >
          <Group justify="space-between" wrap="nowrap" gap="lg">
            <Text size="md" style={{ color: 'light-dark(var(--mantine-color-bad-7), var(--mantine-color-bad-3))' }}>{t.authSignOutFailed}</Text>
            <Button onClick={handleSignOut} style={{
              borderColor: 'light-dark(transparent, var(--mantine-color-brand-3))',
              outlineColor: 'light-dark(var(--mantine-primary-color-filled), var(--mantine-color-brand-3))',
            }}>{t.retry}</Button>
          </Group>
        </Paper>
      ) : signingOut === 'idle' && activeClient.accounts.some((a) => a.isDemo) && (
        // The notice says what kind of company this is - one holding demo accounts - and
        // leaves each figure's provenance to the screen that shows it. It does not wait
        // on a response and does not read one: it holds while the screens load, while no
        // account is checked, and while a request is failing, which are exactly the
        // states in which the numbers on screen are least self-explanatory.
        //
        // The sign-out alert keeps the slot when it has something to say, and the
        // notice is gone the moment sign-out starts, with the rest of the session's
        // content.
        <Paper
          role="status"
          data-testid="demo-notice"
          withBorder
          radius={0}
          p="sm"
          style={{
            // light-dark, the way the shell's own hairlines are written in shell.css:
            // a bare `ink` shade would read as a near-white band in dark mode.
            background: 'light-dark(var(--mantine-color-ink-0), var(--mantine-color-dark-6))',
            borderColor: 'light-dark(var(--mantine-color-ink-1), var(--mantine-color-dark-5))',
            borderLeft: 0,
            borderRight: 0,
          }}
        >
          <Text size="sm">{t.demoNotice}</Text>
        </Paper>
      )}
    >
      {notice !== null && signingOut === 'idle' && (
        <TransitionNotice
          lang={lang}
          notice={notice}
          onSwitch={(id) => {
            handleSelectClient(id)
            onDismissNotice?.()
          }}
          onDismiss={() => onDismissNotice?.()}
        />
      )}

      {/* The creatives screen and the hypotheses screen both render bare on the design
          system: the spike's legacy wrapper went with the rollups view that needed it. */}
      {guest?.unavailable === true && !leaveUnconfirmed && (
        <Paper role="alert" withBorder p="md">
          <Text>{SHARE_COPY[lang].recheckFailed}</Text>
          <Button mt="sm" onClick={() => { void guest?.onRecheck() }}>{t.retry}</Button>
        </Paper>
      )}

      {/* A failed guest re-read or an unconfirmed leave hides the screens rather than
          unmounting them, so their filters, sort and open panel survive until Retry moves
          `reloadKey`; the hidden panel answers no key meanwhile. */}
      <Box className="grv-reporting" style={reporting ? undefined : { display: 'none' }}>
      {screen === 'creatives' && (
        <CreativesScreen
          lang={lang}
          companyId={activeClient.id}
          period={period}
          accountIds={accountIds}
          preset={preset}
          bucket={bucket}
          // An open detail panel closes when the accounts change: after a demo removal
          // the creative it shows may no longer exist.
          rowScopeKey={`${activeClient.id}|r${accountsRevision}`}
          onCompareHypotheses={() => selectScreen('rollups')}
          onWidenPeriod={changePeriod}
          onBounds={onBounds}
          onOldContract={onOldContract}
          onPeriodRefused={onPeriodRefused}
          onOpenPeriod={periodOpenable ? onOpenPeriod : undefined}
          settling={settling}
          onCompanyNotFound={onCompanyNotFound}
          reloadKey={reloadKey}
          suspended={!reporting}
          rangeRefused={rangeRefused}
          bounds={pickerBounds}
          compare={!oldContract}
          scope={scope}
          reads={reads}
          periodPrefetch={screenPrefetch}
          userId={userId}
          onOpenSettings={canEditSettings ? () => selectScreen('settings') : undefined}
          assignment={canEditDictionary ? {
            pending: pendingAssignments[activeClient.id] !== undefined,
            onAssign: assign,
            onCreateAndAssign: createAndAssign,
            onCreateValue: createValue,
            onSetVersion: setVersion,
            onMerge: merge,
            onDecide: decide,
            pickerRevision: pickerRevisions[activeClient.id] ?? 0,
          } : undefined}
          accountToggles={{
            accounts: activeClient.accounts,
            checked: accs,
            onToggle: toggleAcc,
            onSelectAll: () => setAccs((prev) => ({
              ...prev,
              ...Object.fromEntries(activeClient.accounts.map((a) => [a.id, true])),
            })),
          }}
        />
      )}

      {screen === 'rollups' && (
        <HypothesesScreen
          lang={lang}
          companyId={activeClient.id}
          period={period}
          accountIds={accountIds}
          axis={axis}
          onAxisChange={setAxis}
          onOpenBucket={(bucketAxis, id) => {
            setBucket({ axis: bucketAxis, id })
            setScreen('creatives')
          }}
          onBounds={onBounds}
          onOldContract={onOldContract}
          onPeriodRefused={onPeriodRefused}
          onOpenPeriod={periodOpenable ? onOpenPeriod : undefined}
          settling={settling}
          rangeRefused={rangeRefused}
          scope={scope}
          reads={reads}
          reloadKey={reloadKey}
          onManageDictionary={member ? () => selectScreen('dictionary') : undefined}
        />
      )}
      </Box>

      {member && screen === 'dictionary' && (
        <DictionaryScreen
          key={activeClient.id}
          lang={lang}
          companyId={activeClient.id}
          canEdit={canEditDictionary}
          onWrite={writeDictionary}
          // Every dictionary write raises the picker's revision and every assignment the
          // reporting's, so their sum moves on either.
          revision={(pickerRevisions[activeClient.id] ?? 0) + (reportingRevisions[activeClient.id] ?? 0)}
          onAccessLost={reportLost}
          onBack={() => selectScreen('rollups')}
        />
      )}

      {member && screen === 'settings' && (
        <SettingsScreen
          key={activeClient.id}
          lang={lang}
          companyId={activeClient.id}
          onSaved={onSettingsSaved}
          onAccessLost={reportLost}
        />
      )}

      {screen === 'invitations' && invitations !== undefined && onReconcile !== undefined && (
        <InvitationsPage lang={lang} invitations={invitations} onReconcile={onReconcile} />
      )}

      {screen === 'permission' && !approved && (
        <PermissionScreen
          lang={lang}
          onReenter={() => {
            selectScreen('creatives')
            void refreshSession()
          }}
          onBack={() => selectScreen('creatives')}
          clientName={activeClient.name}
        />
      )}

      {member && screen === 'members' && userId !== undefined && (
        <MembersPage
          key={activeClient.id}
          lang={lang}
          client={activeClient}
          userId={userId}
          members={companyMembers.state}
          onReloadMembers={companyMembers.reload}
          onAccessLost={checkAccess}
          onRefreshSession={refreshSession}
          onLeft={leaveCompany}
          onDeleted={companyDeleted}
          deletionHold={user?.companyDeletionHold === true}
          narrow={narrow}
        />
      )}

      {member && screen === 'share' && can(activeClient.role, 'share') && (
        <ShareDialog
          key={activeClient.id}
          lang={lang}
          companyId={activeClient.id}
          companyName={activeClient.name}
          onAccessLost={reportLost}
          onClose={() => selectScreen('creatives')}
        />
      )}
    </AppShell>
  )
}
